import { AnyObject } from "./misc";
import {
  Resource,
  ExamAttempt,
  ExamSession,
  Maybe,
  Objective,
  ResourceFile,
  Scalars,
  ScoringMethodEnum,
} from "./schema";
import { MakeOptional, Replace } from "./util";

export type ParsedExam = Replace<
  Resource,
  {
    data: {
      url: string;
      title?: string;
      description?: string;
      image?: string;
    };
    content?: Maybe<ExamContent>;
    config?: Maybe<ExamConfigData>;
  }
>;

export enum ExamConfigStatus {
  Pending = "PENDING",
  InProgress = "IN_PROGRESS",
  Cancelled = "CANCELLED",
  CompleteNeedsScoring = "COMPLETE_NEEDS_SCORING",
  CompleteSendOutcomes = "COMPLETE_SEND_OUTCOMES",
  Complete = "COMPLETE",
}

export enum ExamSessionStatus {
  Pending = "PENDING",
  InProgress = "IN_PROGRESS",
  Cancelled = "CANCELLED",
  Paused = "PAUSED",
  CompleteNeedsScoring = "COMPLETE_NEEDS_SCORING",
  CompleteSendOutcomes = "COMPLETE_SEND_OUTCOMES",
  CompleteUpdateResults = "COMPLETE_UPDATE_RESULTS",
  Complete = "COMPLETE",
}

export interface Mastery {
  doc_count: number;
  key: number;
  objective: Objective;
  dates: MasteryDate[];
  scoreMax: number;
  scoreActual: number;
  sessionCount: number;
  responseCount: number;
}

export interface MasteryDate {
  key: number;
  doc_count: number;
  scoreMax: number;
  scoreActual: number;
}

export interface Score {
  min: number;
  max: number;
  actual?: number;
  custom?: number;
  isScorable: boolean;
}

export interface ExamScore {
  points: Score;
  items: {
    id: string;
    sk: string;
    name: string;
    score: Score;
    alignmentIds: string[];
  }[];
  objectives: {
    [id: string]: {
      id: string;
      abbreviation: string;
      text: string;
      score: Score;
      scores: Score[];
    };
  };
}

export type NonNullScore = Score & {
  actual: number;
};

export type ExamSessionResultItem = {
  id: string;
  sk: string;
  name: string;
  score: Score;
  alignmentIds: string[];
};

// persisted blob data to s3
export type ExamBlobFiles = {
  [questionId: string]: {
    [interactionId: string]: {
      [blobId: string]: ResourceFile;
    };
  };
};
// loaded blob data from S3
export type ExamBlobs = {
  [questionId: string]: {
    [interactionId: string]: {
      [blobId: string]: ArrayBuffer;
    };
  };
};

export type SerializedHighlight = {
  text: string;
  color: "red" | "yellow" | "blue" | "purple";

  // offsets from the start of the container
  startOffset: number;
  endOffset: number;
};
export type ExamHighlights = {
  [placementId: string]: SerializedHighlight[];
};

export type ExamSessionResults = {
  items: ExamSessionResultItem[];
  objectives: {
    [objectiveId: string]: {
      score: Score;
      id: string;
      text: string;
      abbreviation: string;
      scores: Score[];
    };
  };
  points: Score;
};

export type ParsedExamSession = Replace<
  ExamSession,
  {
    examSessionResponses: AnyObject;
    examSessionResults: ExamSessionResults;
    config: ExamConfigData;
  }
>;

export type ParsedExamAttempt = Replace<
  ExamAttempt,
  {
    examSessionResponses: AnyObject;
    examSessionResults: ExamSessionResults | null;
    examBlobs: ExamBlobFiles | null;
    highlights: ExamHighlights | null;
  }
>;

/**
 * Realize this is still `any` but stubbing this out as a type now, I can begin
 * adding this to our codebase where we're needing a response type. */
export type ExamItemResponse = any;

export type ExamItemResponses = {
  [itemId: string]: ExamItemResponse;
};

export type ParsedExamConfig = Replace<
  Resource,
  {
    config: ExamConfigData;
  }
>;

export type ExamTicket = Replace<
  Resource,
  {
    resource: ParsedExamConfig;
  }
>;

export type ExamAttemptResolutionStrategy = {
  mode: "LATEST" | "HIGHEST";
};

export type ExamConfigData = Partial<{
  allowRetakes: boolean;
  allowRostering: boolean;
  allowSaveAndExit: boolean;
  attemptResolutionStrategy: ExamAttemptResolutionStrategy;
  disableAccessBefore: string;
  disableAccessBeforeEnabled: boolean;
  disableAccessOnOrAfter: string;
  disableAccessOnOrAfterEnabled: boolean;
  disableReadAloudForPassages: boolean;
  enableLiveProctor: boolean;
  enableReadAloud: boolean;
  enableLineReader: boolean;
  enableHighlighter: boolean;
  enableCalculatorType: string;
  enableOptionEliminator: boolean;
  googleClassroomAssignments: GoogleClassroomAssignment[];
  retakeLimit: string;
  retakeLimitEnabled: boolean;
  showAdditionalContext: boolean;
  showCorrectAnswersOnComplete: boolean;
  showLearningReferencesOnComplete: boolean;
  showScoreOnComplete: boolean;
  shuffleItems: boolean;
  shuffleOptions: boolean;
  scoringMethod: ScoringMethodEnum;
}>;
export interface GoogleClassroomAssignment {
  courseWorkId: string;
  course: {
    id: string;
    name: string;
    section?: string;
    room?: string;
  };
  /**
   * @todo Bonfire attaches refresh token to exam-config. This is a temporary place
   * for bonfire demo day, but <LaunchExam /> should be improved to pull it from
   * a more stable place. */
  refreshToken?: string;
}

export type Exam = Pick<
  Resource,
  | "__typename"
  | "content"
  | "version"
  | "schemaVersion"
  | "createdAt"
  | "idInc"
  | "isDeleted"
  | "maturity"
  | "assetIds"
  | "interaction"
  | "name"
  | "workspaceId"
  | "bankId"
  | "updatedAt"
  | "keywords"
  | "sk"
  | "questionIds"
  | "id"
  | "creatorId"
>;

export type Item = Pick<
  Resource,
  | "__typename"
  | "content"
  | "subject"
  | "grade"
  | "schemaVersion"
  | "createdAt"
  | "idInc"
  | "alignmentIds"
  | "maturity"
  | "assetIds"
  | "interaction"
  | "name"
  | "language"
  | "workspaceId"
  | "bankId"
  | "interactionCount"
  | "eventLogActorId"
  | "idVendor"
  | "updatedAt"
  | "keywords"
  | "sk"
  | "courseIds"
  | "id"
  | "tags"
>;

export type ExamSessionMetadata = {
  shuffleOptions: boolean;
  shuffleItems: boolean;
  shuffleSeed: string | null;
};

export type ExamSessionResponses = {
  [itemId: string]: ExamSessionResponse;
};

export type ExamSessionResponse = AnyObject;

export interface ExamContent {
  forms?: Maybe<Array<Maybe<ExamForm>>>;
}

export interface ExamForm {
  sections?: Maybe<Array<Maybe<ExamFormSection>>>;
}

export interface ExamFormSection {
  questionIds?: Maybe<Array<Maybe<Scalars["String"]>>>;
}

export type ExamConfigResource = Pick<
  Resource,
  | "alignmentIds"
  | "appId"
  | "appKey"
  | "config"
  | "createdAt"
  | "creatorId"
  | "id"
  | "name"
  | "resourceId"
  | "schemaVersion"
  | "sk"
  | "updatedAt"
  | "workspaceId"
>;

export type EnhancedExamSessionResults = Replace<
  MakeOptional<ExamSessionResults, "points">,
  {
    items: (MakeOptional<ExamSessionResultItem, "score"> & {
      questions?: any[];
    })[];
  }
>;
